import { useState } from "react";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  HelpCircle, 
  Send, 
  Mail, 
  MessageSquare,
  FileText,
  Users,
  CreditCard,
  Loader2,
  CheckCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_TOPICS = [
  { icon: FileText, label: "Deals & Pipeline", value: "Deals & Pipeline Question" },
  { icon: Users, label: "Team & Collaboration", value: "Team & Collaboration Question" },
  { icon: CreditCard, label: "Billing & Subscription", value: "Billing & Subscription Question" },
  { icon: MessageSquare, label: "Feature Request", value: "Feature Request" },
];

export default function SupportPage({ user, subscription, onLogout }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subject.trim() || !message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setSending(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/support`,
        { subject, message },
        { withCredentials: true }
      );
      setSent(true);
      toast.success("Support request sent!");
    } catch (error) {
      toast.error("Failed to send request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const selectTopic = (topic) => {
    setSubject(topic);
  };

  const resetForm = () => {
    setSubject("");
    setMessage("");
    setSent(false);
  };

  return (
    <div className="min-h-screen bg-black flex">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-heading text-3xl text-white uppercase tracking-wide mb-2">
              Support Center
            </h1>
            <p className="text-white/70">
              Need help? We're here for you.
            </p>
          </div>

          {sent ? (
            /* Success State */
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-8 text-center">
              <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
              </div>
              <h2 className="text-xl text-white font-bold mb-2">Message Sent!</h2>
              <p className="text-white/70 mb-6">
                We've received your request and will respond within 24 hours.
                A confirmation has been sent to your email.
              </p>
              <Button onClick={resetForm} className="gold-gradient-btn">
                Send Another Message
              </Button>
            </div>
          ) : (
            <>
              {/* Quick Topics */}
              <div className="mb-6">
                <p className="text-white/70 text-sm mb-3">Quick Topics</p>
                <div className="grid grid-cols-2 gap-3">
                  {QUICK_TOPICS.map((topic) => (
                    <button
                      key={topic.value}
                      onClick={() => selectTopic(topic.value)}
                      className={`p-4 border text-left transition-all ${
                        subject === topic.value
                          ? "border-gold bg-gold/10"
                          : "border-white/[0.08] bg-[#0A0A0A]/80 backdrop-blur-sm hover:border-white/[0.15]"
                      }`}
                    >
                      <topic.icon className={`w-5 h-5 mb-2 ${
                        subject === topic.value ? "text-gold" : "text-white/70"
                      }`} />
                      <span className={`text-sm ${
                        subject === topic.value ? "text-gold" : "text-white/70"
                      }`}>
                        {topic.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact Form */}
              <form onSubmit={handleSubmit} className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 space-y-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Subject</label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="What do you need help with?"
                    className="bg-black border-white/[0.08] text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-white/70 text-sm mb-2">Message</label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe your issue or question in detail..."
                    rows={6}
                    className="bg-black border-white/[0.08] text-white resize-none"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={sending}
                  className="w-full gold-gradient-btn font-bold"
                >
                  {sending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" />Send Message</>
                  )}
                </Button>
              </form>

              {/* Direct Contact */}
              <div className="mt-6 p-4 bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08]/50 text-center">
                <p className="text-white/70 text-sm mb-2">Or email us directly at</p>
                <a 
                  href="mailto:support@dealforge.pro" 
                  className="text-gold hover:underline flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  support@dealforge.pro
                </a>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
